import MuiButton from '@mui/material/Button';
import type { SxProps, Theme } from '@mui/material/styles';
import is from '@sindresorhus/is';
import type { ComponentProps, PropsWithChildren, ReactElement } from 'react';
import { forwardRef } from 'react';

export type ButtonBaseProps = Pick<
    ComponentProps<typeof MuiButton>,
    'aria-label' | 'color' | 'endIcon' | 'fullWidth' | 'onClick' | 'size' | 'startIcon' | 'type' | 'variant'
> &
    PropsWithChildren<{
        readonly dataCy?: string | undefined;
        readonly href?: string | undefined;
        readonly isDisabled?: boolean | undefined;
        readonly sx?: SxProps<Theme> | undefined;
    }>;

/**
 * Internal button component that can the used by actually exposed button variants
 */

export const ButtonBase = forwardRef<HTMLButtonElement, ButtonBaseProps>((props, ref): ReactElement => {
    const {
        children,
        color,
        dataCy,
        endIcon,
        fullWidth,
        href,
        isDisabled,
        onClick,
        size,
        startIcon,
        sx,
        type,
        variant,
    } = props;

    return (
        <MuiButton
            ref={ref}
            aria-label={props['aria-label']}
            color={color ?? 'secondary'}
            data-cy={dataCy}
            disabled={isDisabled === true}
            endIcon={endIcon}
            fullWidth={fullWidth ?? false}
            href={href ?? ''}
            size={size ?? 'medium'}
            startIcon={startIcon}
            sx={sx ?? {}}
            type={type}
            variant={variant ?? 'text'}
            onClick={is.string(href) ? undefined : onClick}
        >
            {children}
        </MuiButton>
    );
});
